import { BleManager, Device } from 'react-native-ble-plx';
import { StateCreator } from 'zustand';
import { RobotDevice } from '../types';

export type RobotConnEventListener = (robotDevice: RobotDevice) => void;

export enum RobotConnEventType {
  NewSocket,
}

export type AddRobotConnEventListenerFunc = (params: {
  event: RobotConnEventType;
  callback: RobotConnEventListener;
}) => RemoveRobotConnEventListenerFunc;
export type RemoveRobotConnEventListenerFunc = () => void;

export interface RobotStore {
  /**
   * An object map of all existing robot connections.
   */
  robotDevices: {
    [robotId: string]: RobotDevice;
  };

  /****************************************
   * Shared stuff
   ****************************************/

  /**
   * Delete a robot device from the object map.
   * It also disconnects it from the app across all channels.
   */
  deleteRobotDevice: (robotId: string) => Promise<void>;

  /**
   * Connection Event Listeners currently attached to the system
   */
  robotConnEventListeners: {
    [key in RobotConnEventType]: RobotConnEventListener[];
  };
  /**
   * Add a listener for robot connection events
   *
   * @param
   * - event: the event to wait for
   * - callback: the callback to trigger when the event happens
   *
   * @returns A cleanup function that removes the event listener
   */
  addRobotConnEventListener: AddRobotConnEventListenerFunc;

  /**
   * Helper function to check whether the robot is connected or not
   * @returns boolean as to whether the robot is connected
   */
  isConnected: (
    robotId: string
  ) => 'connected' | 'connecting' | 'disconnecting' | 'disconnected';

  /****************************************
   * All the websocket stuff
   ****************************************/

  /**
   * Call this function to connect to a robot device
   *
   * The connection will keep retrying to connect with an expontential backoff until .disconnect() is called.
   * The disconnect method can be found from the useRobotConnection hook
   *
   * If a previous connection already exists, and is still connected, then nothing happens.
   * In this case, the old connection can be force recreated using params.forceReconnection.
   *
   * @param robotId
   * @param robotUrl
   * @param params
   * @returns
   */
  establishRobotWebsocketConnection: (
    robotDevice: RobotDevice,
    robotUrl: string,
    params?: { forceReconnection: boolean }
  ) => void;

  /****************************************
   * All the bluetooth stuff
   ****************************************/

  BleManager: BleManager | null;

  /**
   *
   */
  establishRobotBluetoothConnection: (
    robotDevice: RobotDevice,
    bluetoothDevice: Device
  ) => Promise<boolean>;
}

export type RobotState = RobotStore;

/** A slice of the robot state */
export type RobotStateSlice<T> = StateCreator<
  RobotState,
  [['zustand/immer', never]],
  [],
  T
>;
